import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { api } from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Building2, Users, Target, TrendingUp, Plus, Search, Filter,
  Phone, Mail, Linkedin, ChevronRight, DollarSign, Calendar,
  CheckCircle2, AlertCircle, Clock, MoreHorizontal, GripVertical,
  BarChart3, CheckSquare, FileText
} from 'lucide-react';
import CompanyModal from '@/components/crm/CompanyModal';
import ContactModal from '@/components/crm/ContactModal';
import OpportunityModal from '@/components/crm/OpportunityModal';
import ActivityModal from '@/components/crm/ActivityModal';
import CRMTaskBoard from '@/components/crm/CRMTaskBoard';
import CRMTaskModal from '@/components/crm/CRMTaskModal';
import RevenueIntelligence from '@/components/crm/RevenueIntelligence';
import QuoteModal from '@/components/crm/QuoteModal';

const PIPELINE_STAGES = [
  { id: 'Lead Identified', label: 'Lead', color: 'bg-slate-500', probability: 10 },
  { id: 'Intro Call Held', label: 'Intro Call', color: 'bg-blue-500', probability: 20 },
  { id: 'Needs Identified', label: 'Needs ID', color: 'bg-indigo-500', probability: 40 },
  { id: 'Proposal Sent', label: 'Proposal', color: 'bg-purple-500', probability: 60 },
  { id: 'Negotiation', label: 'Negotiation', color: 'bg-amber-500', probability: 80 },
  { id: 'Awarded', label: 'Won', color: 'bg-green-500', probability: 100 },
  { id: 'Lost', label: 'Lost', color: 'bg-red-500', probability: 0 },
];

const formatCurrency = (value) => {
  if (value >= 1000000) return `R${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `R${(value / 1000).toFixed(0)}K`;
  return `R${value.toFixed(0)}`;
};

const CRMDashboard = () => {
  const [activeTab, setActiveTab] = useState('pipeline');
  const [dashboard, setDashboard] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [companyModal, setCompanyModal] = useState({ open: false, company: null });
  const [contactModal, setContactModal] = useState({ open: false, contact: null });
  const [opportunityModal, setOpportunityModal] = useState({ open: false, opportunity: null });
  const [activityModal, setActivityModal] = useState({ open: false, context: null });
  const [taskModal, setTaskModal] = useState({ open: false, task: null });
  const [quoteModal, setQuoteModal] = useState({ open: false, quote: null, opportunity: null });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [dashRes, oppsRes, compRes, contRes, actRes, quotesRes] = await Promise.all([
        api.crm.getDashboard(),
        api.crm.getOpportunities({ exclude_closed: false }),
        api.crm.getCompanies(),
        api.crm.getContacts(),
        api.crm.getActivities({ limit: 20 }),
        api.crm.getQuotes(),
      ]);
      setDashboard(dashRes.data);
      setOpportunities(oppsRes.data);
      setCompanies(compRes.data);
      setContacts(contRes.data);
      setActivities(actRes.data);
      setQuotes(quotesRes.data);
    } catch (error) {
      toast.error('Failed to load CRM data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleStageChange = async (opportunityId, newStage) => {
    try {
      await api.crm.updateOpportunity(opportunityId, { stage: newStage });
      toast.success('Deal moved successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to update deal');
    }
  };

  const handleDragStart = (e, opportunity) => {
    e.dataTransfer.setData('opportunityId', opportunity.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, stageId) => {
    e.preventDefault();
    const opportunityId = e.dataTransfer.getData('opportunityId');
    if (opportunityId) {
      handleStageChange(opportunityId, stageId);
    }
  };

  const filteredOpportunities = opportunities.filter(opp => 
    !searchTerm || 
    opp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    opp.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStageOpportunities = (stageId) => 
    filteredOpportunities.filter(opp => opp.stage === stageId);

  const getStageValue = (stageId) => 
    getStageOpportunities(stageId).reduce((sum, opp) => sum + (opp.estimated_value || 0), 0);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-lg font-mono">Loading CRM...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="h-full flex flex-col bg-slate-50">
        {/* Header */}
        <div className="bg-white border-b px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Sales Pipeline</h1>
              <p className="text-sm text-slate-500">Manage your deals and client relationships</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCompanyModal({ open: true, company: null })}
                data-testid="add-company-btn"
              >
                <Building2 className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Company</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setContactModal({ open: true, contact: null })}
                data-testid="add-contact-btn"
              >
                <Users className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Contact</span>
              </Button>
              <Button 
                size="sm"
                onClick={() => setOpportunityModal({ open: true, opportunity: null })}
                data-testid="add-deal-btn"
              >
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">New Deal</span>
              </Button>
            </div>
          </div>
          
          {/* Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
            <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white border-0">
              <CardContent className="p-2.5 sm:p-4">
                <p className="text-[10px] sm:text-xs text-slate-300 uppercase tracking-wider">Pipeline</p>
                <p className="text-base sm:text-2xl font-bold mt-0.5">{formatCurrency(dashboard?.pipeline_value || 0)}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-600 to-green-700 text-white border-0">
              <CardContent className="p-2.5 sm:p-4">
                <p className="text-[10px] sm:text-xs text-green-200 uppercase tracking-wider">Won This Month</p>
                <p className="text-base sm:text-2xl font-bold mt-0.5">{formatCurrency(dashboard?.won_value_this_month || 0)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-2.5 sm:p-4">
                <p className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wider">Weighted</p>
                <p className="text-base sm:text-2xl font-bold text-slate-900 mt-0.5">{formatCurrency(dashboard?.weighted_pipeline || 0)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-2.5 sm:p-4">
                <p className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wider">Win Rate</p>
                <p className="text-base sm:text-2xl font-bold text-slate-900 mt-0.5">{dashboard?.conversion_rate?.toFixed(0) || 0}%</p>
              </CardContent>
            </Card>
            <Card className="col-span-2 sm:col-span-1">
              <CardContent className="p-2.5 sm:p-4">
                <p className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wider">Avg Deal</p>
                <p className="text-base sm:text-2xl font-bold text-slate-900 mt-0.5">{formatCurrency(dashboard?.avg_deal_size || 0)}</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <div className="bg-white border-b px-4 sm:px-6 overflow-x-auto scrollbar-hide">
            <TabsList className="h-12 bg-transparent gap-3 sm:gap-6 w-max">
              <TabsTrigger 
                value="pipeline" 
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-indigo-600 data-[state=active]:text-indigo-600 rounded-none px-0 text-xs sm:text-sm whitespace-nowrap"
              >
                <Target className="h-4 w-4 mr-1 sm:mr-2 hidden sm:block" />
                Pipeline
              </TabsTrigger>
              <TabsTrigger 
                value="companies"
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-indigo-600 data-[state=active]:text-indigo-600 rounded-none px-0 text-xs sm:text-sm whitespace-nowrap"
              >
                <Building2 className="h-4 w-4 mr-1 sm:mr-2 hidden sm:block" />
                Companies ({companies.length})
              </TabsTrigger>
              <TabsTrigger 
                value="contacts"
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-indigo-600 data-[state=active]:text-indigo-600 rounded-none px-0 text-xs sm:text-sm whitespace-nowrap"
              >
                <Users className="h-4 w-4 mr-1 sm:mr-2 hidden sm:block" />
                Contacts ({contacts.length})
              </TabsTrigger>
              <TabsTrigger 
                value="activities"
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-indigo-600 data-[state=active]:text-indigo-600 rounded-none px-0 text-xs sm:text-sm whitespace-nowrap"
              >
                <Clock className="h-4 w-4 mr-1 sm:mr-2 hidden sm:block" />
                Activities
              </TabsTrigger>
              <TabsTrigger 
                value="tasks"
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-indigo-600 data-[state=active]:text-indigo-600 rounded-none px-0 text-xs sm:text-sm whitespace-nowrap"
              >
                <CheckSquare className="h-4 w-4 mr-1 sm:mr-2 hidden sm:block" />
                Tasks
              </TabsTrigger>
              <TabsTrigger 
                value="intelligence"
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-indigo-600 data-[state=active]:text-indigo-600 rounded-none px-0 text-xs sm:text-sm whitespace-nowrap"
              >
                <BarChart3 className="h-4 w-4 mr-1 sm:mr-2 hidden sm:block" />
                Intelligence
              </TabsTrigger>
              <TabsTrigger 
                value="quotes"
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-indigo-600 data-[state=active]:text-indigo-600 rounded-none px-0 text-xs sm:text-sm whitespace-nowrap"
                data-testid="quotes-tab"
              >
                <FileText className="h-4 w-4 mr-1 sm:mr-2 hidden sm:block" />
                Quotes ({quotes.length})
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Pipeline View */}
          <TabsContent value="pipeline" className="flex-1 m-0 overflow-hidden">
            <div className="p-4 bg-white border-b">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search deals..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            {/* Mobile: Accordion/List View */}
            <div className="sm:hidden flex-1 overflow-auto p-3 space-y-2" data-testid="pipeline-mobile">
              {PIPELINE_STAGES.filter(s => s.id !== 'Lost').map((stage) => {
                const stageOpps = getStageOpportunities(stage.id);
                return (
                  <div key={stage.id} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                    <div className="flex items-center justify-between p-3 bg-slate-50 border-b">
                      <div className="flex items-center gap-2">
                        <div className={`w-2.5 h-2.5 rounded-full ${stage.color}`} />
                        <span className="font-medium text-sm text-slate-700">{stage.label}</span>
                        <Badge variant="secondary" className="text-xs">{stageOpps.length}</Badge>
                      </div>
                      <span className="text-sm font-semibold text-slate-900">{formatCurrency(getStageValue(stage.id))}</span>
                    </div>
                    {stageOpps.length > 0 && (
                      <div className="divide-y divide-slate-100">
                        {stageOpps.map(opp => (
                          <div
                            key={opp.id}
                            onClick={() => setOpportunityModal({ open: true, opportunity: opp })}
                            className="p-3 cursor-pointer active:bg-slate-50"
                            data-testid={`deal-mobile-${opp.id}`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="font-medium text-sm text-slate-900 line-clamp-1 flex-1 mr-2">{opp.name}</h4>
                              <span className="font-semibold text-sm text-slate-900 shrink-0">{formatCurrency(opp.estimated_value || 0)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              {opp.company_name ? (
                                <p className="text-xs text-slate-500 flex items-center gap-1"><Building2 className="h-3 w-3" />{opp.company_name}</p>
                              ) : <span />}
                              {opp.expected_close_date && (
                                <span className="text-xs text-slate-400">{new Date(opp.expected_close_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                              )}
                            </div>
                            {opp.stage === 'Awarded' && (
                              <Button variant="outline" size="sm" className="mt-2 w-full text-xs h-7" onClick={(e) => { e.stopPropagation(); setQuoteModal({ open: true, quote: null, opportunity: opp }); }}>
                                <FileText className="h-3 w-3 mr-1" />Create Quote
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Desktop: Kanban Board */}
            <div className="hidden sm:flex flex-1 overflow-x-auto p-3 sm:p-4">
              <div className="flex gap-2.5 sm:gap-4 min-w-max h-full">
                {PIPELINE_STAGES.filter(s => s.id !== 'Lost').map((stage) => (
                  <div 
                    key={stage.id}
                    className="w-48 sm:w-72 flex flex-col bg-slate-100 rounded-lg shrink-0"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, stage.id)}
                  >
                    {/* Stage Header */}
                    <div className="p-3 border-b border-slate-200">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${stage.color}`}></div>
                          <span className="font-medium text-sm text-slate-700">{stage.label}</span>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {getStageOpportunities(stage.id).length}
                        </Badge>
                      </div>
                      <p className="text-sm font-semibold text-slate-900">
                        {formatCurrency(getStageValue(stage.id))}
                      </p>
                    </div>
                    
                    {/* Stage Cards */}
                    <div className="flex-1 overflow-y-auto p-2 space-y-2">
                      {getStageOpportunities(stage.id).map((opp) => (
                        <div
                          key={opp.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, opp)}
                          onClick={() => setOpportunityModal({ open: true, opportunity: opp })}
                          className="bg-white rounded-lg p-3 shadow-sm border border-slate-200 cursor-pointer hover:shadow-md hover:border-indigo-300 transition-all group"
                          data-testid={`deal-card-${opp.id}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <GripVertical className="h-4 w-4 text-slate-300 opacity-0 group-hover:opacity-100 cursor-grab" />
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </div>
                          <h4 className="font-medium text-sm text-slate-900 mb-1 line-clamp-2">
                            {opp.name}
                          </h4>
                          {opp.company_name && (
                            <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              {opp.company_name}
                            </p>
                          )}
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-sm text-slate-900">
                              {formatCurrency(opp.estimated_value || 0)}
                            </span>
                            {opp.expected_close_date && (
                              <span className="text-xs text-slate-400 flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {new Date(opp.expected_close_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                              </span>
                            )}
                          </div>
                          {opp.days_in_stage > 14 && (
                            <div className="mt-2 flex items-center gap-1 text-amber-600 text-xs">
                              <AlertCircle className="h-3 w-3" />
                              {opp.days_in_stage} days in stage
                            </div>
                          )}
                          {opp.stage === 'Awarded' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-2 w-full text-xs h-7"
                              onClick={(e) => {
                                e.stopPropagation();
                                setQuoteModal({ open: true, quote: null, opportunity: opp });
                              }}
                              data-testid={`create-quote-from-deal-${opp.id}`}
                            >
                              <FileText className="h-3 w-3 mr-1" />
                              Create Quote
                            </Button>
                          )}
                        </div>
                      ))}
                      {getStageOpportunities(stage.id).length === 0 && (
                        <div className="text-center py-8 text-slate-400 text-sm">
                          No deals
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Companies View */}
          <TabsContent value="companies" className="flex-1 m-0 overflow-auto p-4 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {companies.map((company) => (
                <Card 
                  key={company.id} 
                  className="cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => setCompanyModal({ open: true, company })}
                  data-testid={`company-card-${company.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{company.name}</h3>
                          {company.industry && (
                            <p className="text-xs text-slate-500">{company.industry}</p>
                          )}
                        </div>
                      </div>
                      <Badge 
                        variant={company.relationship_strength === 'hot' ? 'default' : 'secondary'}
                        className={
                          company.relationship_strength === 'hot' ? 'bg-red-500' :
                          company.relationship_strength === 'warm' ? 'bg-amber-500' :
                          company.relationship_strength === 'cold' ? 'bg-blue-500' : ''
                        }
                      >
                        {company.relationship_strength}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <p className="text-slate-500 text-xs">Pipeline</p>
                        <p className="font-medium">{formatCurrency(company.pipeline_value || 0)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Revenue</p>
                        <p className="font-medium">{formatCurrency(company.total_revenue || 0)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Contacts</p>
                        <p className="font-medium">{company.contact_count || 0}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Active Deals</p>
                        <p className="font-medium">{company.active_opportunities || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {companies.length === 0 && (
                <div className="col-span-full text-center py-12 text-slate-400">
                  <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No companies yet. Add your first company to get started.</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Contacts View */}
          <TabsContent value="contacts" className="flex-1 m-0 overflow-auto p-4 sm:p-6">
            {/* Mobile: Card layout */}
            <div className="sm:hidden space-y-3">
              {contacts.map((contact) => (
                <Card
                  key={contact.id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => setContactModal({ open: true, contact })}
                  data-testid={`contact-card-${contact.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-medium text-sm shrink-0">
                        {contact.first_name?.[0]}{contact.last_name?.[0]}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900 truncate">{contact.full_name}</p>
                        <p className="text-xs text-slate-500 truncate">{contact.company_name || 'No company'}</p>
                      </div>
                      <Badge
                        variant="secondary"
                        className={`shrink-0 text-xs ${
                          contact.relationship_strength === 'hot' ? 'bg-red-100 text-red-700' :
                          contact.relationship_strength === 'warm' ? 'bg-amber-100 text-amber-700' :
                          'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {contact.relationship_strength}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-500">
                      {contact.role && <span className="truncate">{contact.role}</span>}
                      {contact.is_decision_maker && <Badge variant="secondary" className="text-xs shrink-0">DM</Badge>}
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      {contact.email && (
                        <a href={`mailto:${contact.email}`} onClick={(e) => e.stopPropagation()} className="text-slate-400 hover:text-indigo-600">
                          <Mail className="h-4 w-4" />
                        </a>
                      )}
                      {contact.phone && (
                        <a href={`tel:${contact.phone}`} onClick={(e) => e.stopPropagation()} className="text-slate-400 hover:text-indigo-600">
                          <Phone className="h-4 w-4" />
                        </a>
                      )}
                      {contact.linkedin && (
                        <a href={contact.linkedin} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-slate-400 hover:text-indigo-600">
                          <Linkedin className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {contacts.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No contacts yet. Add your first contact to get started.</p>
                </div>
              )}
            </div>
            {/* Desktop: Table layout */}
            <div className="hidden sm:block bg-white rounded-lg border overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Name</th>
                    <th className="text-left p-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Company</th>
                    <th className="text-left p-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Role</th>
                    <th className="text-left p-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Contact</th>
                    <th className="text-left p-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Relationship</th>
                    <th className="text-left p-4 text-xs font-semibold text-slate-600 uppercase tracking-wider"></th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((contact) => (
                    <tr 
                      key={contact.id} 
                      className="border-b hover:bg-slate-50 cursor-pointer"
                      onClick={() => setContactModal({ open: true, contact })}
                      data-testid={`contact-row-${contact.id}`}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-medium text-sm">
                            {contact.first_name?.[0]}{contact.last_name?.[0]}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{contact.full_name}</p>
                            {contact.is_decision_maker && (
                              <Badge variant="secondary" className="text-xs">Decision Maker</Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-sm text-slate-600">{contact.company_name || '-'}</td>
                      <td className="p-4 text-sm text-slate-600">{contact.role || contact.custom_role || '-'}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {contact.email && (
                            <a href={`mailto:${contact.email}`} onClick={(e) => e.stopPropagation()} className="text-slate-400 hover:text-indigo-600">
                              <Mail className="h-4 w-4" />
                            </a>
                          )}
                          {contact.phone && (
                            <a href={`tel:${contact.phone}`} onClick={(e) => e.stopPropagation()} className="text-slate-400 hover:text-indigo-600">
                              <Phone className="h-4 w-4" />
                            </a>
                          )}
                          {contact.linkedin && (
                            <a href={contact.linkedin} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-slate-400 hover:text-indigo-600">
                              <Linkedin className="h-4 w-4" />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge 
                          variant="secondary"
                          className={
                            contact.relationship_strength === 'hot' ? 'bg-red-100 text-red-700' :
                            contact.relationship_strength === 'warm' ? 'bg-amber-100 text-amber-700' :
                            contact.relationship_strength === 'cold' ? 'bg-blue-100 text-blue-700' : ''
                          }
                        >
                          {contact.relationship_strength}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <ChevronRight className="h-4 w-4 text-slate-300" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {contacts.length === 0 && (
                <div className="text-center py-12 text-slate-400">

          {/* Activities View */}
                  <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No contacts yet. Add your first contact to get started.</p>
                </div>
              )}
            </div>
          </TabsContent>
          <TabsContent value="activities" className="flex-1 m-0 overflow-auto p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base sm:text-lg font-semibold">Recent Activities</h2>
              <Button size="sm" onClick={() => setActivityModal({ open: true, context: null })}>
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Log Activity</span>
              </Button>
            </div>
            <div className="space-y-3">
              {activities.map((activity) => (
                <Card key={activity.id} className="hover:shadow-sm transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        activity.activity_type === 'Call' ? 'bg-green-100 text-green-600' :
                        activity.activity_type === 'Email' ? 'bg-blue-100 text-blue-600' :
                        activity.activity_type === 'Meeting' ? 'bg-purple-100 text-purple-600' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {activity.activity_type === 'Call' ? <Phone className="h-5 w-5" /> :
                         activity.activity_type === 'Email' ? <Mail className="h-5 w-5" /> :
                         <Calendar className="h-5 w-5" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-slate-900">{activity.subject}</h4>
                          <span className="text-xs text-slate-400">
                            {activity.activity_date ? new Date(activity.activity_date).toLocaleDateString() : 'Today'}
                          </span>
                        </div>
                        {activity.description && (
                          <p className="text-sm text-slate-600 mt-1 line-clamp-2">{activity.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          {activity.company_name && (
                            <span className="flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              {activity.company_name}
                            </span>
                          )}
                          {activity.contact_name && (
                            <span className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {activity.contact_name}
                            </span>
                          )}
                          {activity.created_by_name && (
                            <span>by {activity.created_by_name}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {activities.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No activities logged yet.</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Tasks View */}
          <TabsContent value="tasks" className="flex-1 m-0 overflow-auto">
            <CRMTaskBoard
              companies={companies}
              contacts={contacts}
              opportunities={opportunities}
              onCreateTask={() => setTaskModal({ open: true, task: null })}
            />
          </TabsContent>

          {/* Intelligence View */}
          <TabsContent value="intelligence" className="flex-1 m-0 overflow-auto">
            <RevenueIntelligence
              dashboard={dashboard}
              opportunities={opportunities}
            />
          </TabsContent>

          {/* Quotes View */}
          <TabsContent value="quotes" className="flex-1 m-0 overflow-auto p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base sm:text-lg font-semibold">Quotes</h2>
              <Button size="sm" onClick={() => setQuoteModal({ open: true, quote: null, opportunity: null })} data-testid="create-quote-btn">
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">New Quote</span>
              </Button>
            </div>
            {/* Mobile: Card layout */}
            <div className="sm:hidden space-y-3">
              {quotes.map((q) => (
                <Card
                  key={q.id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => setQuoteModal({ open: true, quote: q, opportunity: null })}
                  data-testid={`quote-card-${q.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-sm text-indigo-600 font-medium">{q.quote_number}</span>
                      <Badge
                        variant="secondary"
                        className={
                          q.status === 'accepted' ? 'bg-green-100 text-green-700' :
                          q.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                          q.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          q.status === 'expired' ? 'bg-slate-100 text-slate-500' :
                          'bg-amber-100 text-amber-700'
                        }
                      >
                        {q.status}
                      </Badge>
                    </div>
                    <p className="font-medium text-slate-900 text-sm line-clamp-1">{q.title}</p>
                    {q.company_name && <p className="text-xs text-slate-500 mt-0.5">{q.company_name}</p>}
                    <div className="grid grid-cols-2 gap-2 mt-3 pt-2 border-t">
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase">Monthly</p>
                        <p className="text-sm font-semibold">R {(q.monthly_total || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase">Yearly</p>
                        <p className="text-sm font-semibold">R {(q.yearly_discounted_total || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</p>
                        {q.discount_percent > 0 && (
                          <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 mt-0.5">{q.discount_percent}% off</Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {quotes.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No quotes yet. Create your first quote.</p>
                </div>
              )}
            </div>
            {/* Desktop: Table layout */}
            <div className="hidden sm:block bg-white rounded-lg border overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Quote #</th>
                    <th className="text-left p-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Title</th>
                    <th className="text-left p-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Company</th>
                    <th className="text-left p-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Monthly</th>
                    <th className="text-left p-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Yearly (Discounted)</th>
                    <th className="text-left p-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                    <th className="text-left p-4 text-xs font-semibold text-slate-600 uppercase tracking-wider"></th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.map((q) => (
                    <tr
                      key={q.id}
                      className="border-b hover:bg-slate-50 cursor-pointer"
                      onClick={() => setQuoteModal({ open: true, quote: q, opportunity: null })}
                      data-testid={`quote-row-${q.id}`}
                    >
                      <td className="p-4 font-mono text-sm text-indigo-600">{q.quote_number}</td>
                      <td className="p-4">
                        <p className="font-medium text-slate-900">{q.title}</p>
                        {q.opportunity_name && <p className="text-xs text-slate-500">{q.opportunity_name}</p>}
                      </td>
                      <td className="p-4 text-sm text-slate-600">{q.company_name || '-'}</td>
                      <td className="p-4 text-sm font-medium">R {(q.monthly_total || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</td>
                      <td className="p-4 text-sm font-medium">
                        <span>R {(q.yearly_discounted_total || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</span>
                        {q.discount_percent > 0 && (
                          <Badge variant="secondary" className="ml-2 text-xs bg-green-100 text-green-700">{q.discount_percent}% off</Badge>
                        )}
                      </td>
                      <td className="p-4">
                        <Badge
                          variant="secondary"
                          className={
                            q.status === 'accepted' ? 'bg-green-100 text-green-700' :
                            q.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                            q.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            q.status === 'expired' ? 'bg-slate-100 text-slate-500' :
                            'bg-amber-100 text-amber-700'
                          }
                        >
                          {q.status}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <ChevronRight className="h-4 w-4 text-slate-300" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {quotes.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No quotes yet. Create your first quote from an awarded deal or manually.</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modals */}
      <CompanyModal 
        open={companyModal.open}
        company={companyModal.company}
        onClose={() => setCompanyModal({ open: false, company: null })}
        onSave={loadData}
      />
      <ContactModal 
        open={contactModal.open}
        contact={contactModal.contact}
        companies={companies}
        onClose={() => setContactModal({ open: false, contact: null })}
        onSave={loadData}
      />
      <OpportunityModal 
        open={opportunityModal.open}
        opportunity={opportunityModal.opportunity}
        companies={companies}
        contacts={contacts}
        stages={PIPELINE_STAGES}
        onClose={() => setOpportunityModal({ open: false, opportunity: null })}
        onSave={loadData}
      />
      <ActivityModal
        open={activityModal.open}
        context={activityModal.context}
        companies={companies}
        contacts={contacts}
        opportunities={opportunities}
        onClose={() => setActivityModal({ open: false, context: null })}
        onSave={loadData}
      />
      <CRMTaskModal
        open={taskModal.open}
        task={taskModal.task}
        companies={companies}
        contacts={contacts}
        opportunities={opportunities}
        onClose={() => setTaskModal({ open: false, task: null })}
        onSave={loadData}
      />
      <QuoteModal
        open={quoteModal.open}
        quote={quoteModal.quote}
        opportunity={quoteModal.opportunity}
        companies={companies}
        contacts={contacts}
        onClose={() => setQuoteModal({ open: false, quote: null, opportunity: null })}
        onSave={loadData}
      />
    </DashboardLayout>
  );
};

export default CRMDashboard;
